import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Bookmark, Star, MapPin, Trash2, Loader2, Search, ExternalLink, Building
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { DashboardLayout } from '../components/layout/DashboardLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const EXTERNAL_DIRECTORY_URL = process.env.REACT_APP_EXTERNAL_DIRECTORY_URL || 'https://search.aihorsepowered.com';

export default function BookmarksPage() {
  const [bookmarks, setBookmarks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  const token = localStorage.getItem('token');

  useEffect(() => {
    loadBookmarks();
  }, []);

  const loadBookmarks = async () => {
    try {
      const res = await fetch(`${API_URL}/api/social/bookmarks`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setBookmarks(await res.json());
      }
    } catch (error) {
      console.error('Failed to load bookmarks:', error);
    } finally {
      setLoading(false);
    }
  };

  const removeBookmark = async (businessId) => {
    setDeletingId(businessId);
    try {
      const res = await fetch(`${API_URL}/api/social/bookmarks/${businessId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setBookmarks(prev => prev.filter(b => b.business_id !== businessId));
        toast.success('Bookmark removed');
      }
    } catch (error) {
      toast.error('Failed to remove bookmark');
    } finally {
      setDeletingId(null);
    }
  };

  const filteredBookmarks = bookmarks.filter(b =>
    b.business_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    b.business_category?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <DashboardLayout>
      <div className="p-6 pt-8" data-testid="bookmarks-page">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-2">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center">
              <Bookmark className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">My Bookmarks</h1>
              <p className="text-gray-400">
                {bookmarks.length} saved {bookmarks.length === 1 ? 'business' : 'businesses'}
              </p>
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              placeholder="Search bookmarks..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-gray-500"
            />
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-amber-400" />
          </div>
        ) : bookmarks.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-20 h-20 rounded-full bg-white/5 flex items-center justify-center mx-auto mb-4">
              <Bookmark className="w-10 h-10 text-gray-600" />
            </div>
            <h3 className="text-lg font-medium text-white mb-2">No bookmarks yet</h3>
            <p className="text-gray-400 mb-6 max-w-sm mx-auto">
              Start exploring businesses in our directory and bookmark your favorites!
            </p>
            <a href={EXTERNAL_DIRECTORY_URL} target="_blank" rel="noopener noreferrer">
              <Button className="bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white">
                Explore Directory
              </Button>
            </a>
          </div>
        ) : filteredBookmarks.length === 0 ? (
          <div className="text-center py-16">
            <Search className="w-12 h-12 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400">No bookmarks match your search</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredBookmarks.map((bookmark, index) => (
              <motion.div
                key={bookmark.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="bg-[#0d1525] rounded-xl border border-white/10 overflow-hidden hover:border-amber-500/30 transition-colors group"
                data-testid={`bookmark-${bookmark.business_id}`}
              >
                {/* Image or Placeholder */}
                <div className="aspect-video relative bg-gradient-to-br from-amber-500/10 to-orange-500/10">
                  {bookmark.business_image ? (
                    <img 
                      src={bookmark.business_image} 
                      alt={bookmark.business_name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Building className="w-12 h-12 text-amber-500/50" />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0d1525] via-transparent to-transparent" />
                  
                  {/* Remove Button */}
                  <button
                    onClick={() => removeBookmark(bookmark.business_id)}
                    disabled={deletingId === bookmark.business_id}
                    className="absolute top-3 right-3 p-2 rounded-lg bg-black/50 text-white hover:bg-red-500 transition-colors opacity-0 group-hover:opacity-100"
                  >
                    {deletingId === bookmark.business_id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                  </button>
                </div>

                {/* Content */}
                <div className="p-4">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <h3 className="font-semibold text-white line-clamp-1">{bookmark.business_name}</h3>
                    {bookmark.business_rating > 0 && (
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                        <span className="text-sm text-white">{bookmark.business_rating.toFixed(1)}</span>
                      </div>
                    )}
                  </div>
                  
                  {bookmark.business_category && (
                    <p className="text-sm text-gray-400 capitalize mb-3">
                      {bookmark.business_category.replace(/-/g, ' ')}
                    </p>
                  )}

                  {bookmark.notes && (
                    <p className="text-xs text-gray-500 italic mb-3 line-clamp-2">
                      "{bookmark.notes}"
                    </p>
                  )}

                  <div className="flex items-center justify-between pt-3 border-t border-white/5">
                    <span className="text-xs text-gray-500">
                      Saved {new Date(bookmark.created_at).toLocaleDateString()}
                    </span>
                    <a href={`${EXTERNAL_DIRECTORY_URL}/business/${bookmark.business_id}`} target="_blank" rel="noopener noreferrer">
                      <Button variant="ghost" size="sm" className="text-amber-400 hover:text-amber-300 hover:bg-amber-500/10">
                        View <ExternalLink className="w-3 h-3 ml-1" />
                      </Button>
                    </a>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
